import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, ValidationError } from '@formspree/react';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import styles from './Corporate.module.css';

function useFadeIn(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── CUSTOM FORM MODAL ────────────────────────────────────────────────────────
function TripForm({ onClose }) {
  const { data } = useSite();
  const [state, handleSubmit] = useForm('mqeweyez');
  const [formValues, setFormValues] = useState({ company: '', phone: '', employees: '', idea: '' });
  const set = (k, v) => setFormValues(f => ({ ...f, [k]: v }));

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose} aria-label="Close">✕</button>

        {state.succeeded ? (
          <div className={styles.successState}>
            <div className={styles.successIcon}>🎉</div>
            <h3>Request Received!</h3>
            <p>We'll get back to you within 24 hours to start planning your experience.</p>
            <button className={styles.doneBtn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className={styles.modalHeader}>
              <span className={styles.modalLabel}>Corporate</span>
              <h2 className={styles.modalTitle}>Customize Your Own Trip</h2>
              <p className={styles.modalSub}>Tell us about your team and we'll design the perfect experience.</p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <input type="hidden" name="_subject" value="New Corporate Trip Inquiry – Keepers Experience" />
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Company Name<span>*</span></label>
                  <input
                    className={styles.formInput}
                    type="text"
                    placeholder="e.g. Keepers Corp"
                    name="Company Name"
                    value={formValues.company}
                    onChange={e => set('company', e.target.value)}
                    required
                  />
                  <ValidationError field="Company Name" errors={state.errors} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Phone<span>*</span></label>
                  <input
                    className={styles.formInput}
                    type="tel"
                    placeholder="+962 77 611 3530"
                    name="Phone Number"
                    value={formValues.phone}
                    onChange={e => set('phone', e.target.value)}
                    required
                  />
                  <ValidationError field="Phone Number" errors={state.errors} />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email<span>*</span></label>
                  <input
                    className={styles.formInput}
                    type="email"
                    placeholder="you@company.com"
                    name="Email"
                    required
                  />
                  <ValidationError field="Email" errors={state.errors} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>No. of Employees<span>*</span></label>
                  <select
                    className={styles.formInput}
                    name="Number of Employees"
                    value={formValues.employees}
                    onChange={e => set('employees', e.target.value)}
                    required
                  >
                    <option value="">Select range</option>
                    <option value="1-10">1 – 10</option>
                    <option value="11-30">11 – 30</option>
                    <option value="31-60">31 – 60</option>
                    <option value="61-100">61 – 100</option>
                    <option value="100+">100+</option>
                  </select>
                  <ValidationError field="employees" errors={state.errors} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>What do you have in mind?</label>
                <textarea
                  className={`${styles.formInput} ${styles.formTextarea}`}
                  placeholder="Tell us about your goals, preferred location, budget, or any special requests…"
                  name="Trip Ideas"
                  value={formValues.idea}
                  onChange={e => set('idea', e.target.value)}
                  rows={4}
                />
                <ValidationError field="Trip Ideas" errors={state.errors} />
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.submitBtn} disabled={state.submitting}>
                  Send email
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.whatsappAlt}
                  onClick={() => {
                    const phone = (data.contact?.whatsapp || '').replace(/[^0-9]/g, '');
                    const msg = encodeURIComponent(
                      `Hello Keepers! 👋\nI'd like to plan a *custom corporate trip*.\n\nCompany: ${formValues.company || '–'}\nPhone: ${formValues.phone || '–'}\nEmployees: ${formValues.employees || '–'}\nIdeas: ${formValues.idea || '–'}`
                    );
                    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Send via WhatsApp
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Corporate() {
  const { data } = useSite();
  const navigate = useNavigate();
  const corp = data.corporate;

  const [heroRef, heroVisible] = useFadeIn(0.05);
  const [servicesRef, servicesVisible] = useFadeIn();
  const [packagesRef, packagesVisible] = useFadeIn();
  const [clientsRef, clientsVisible] = useFadeIn();
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const bookPackageWhatsApp = (pkg) => {
    const phone = (data.contact?.whatsapp || '').replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(
      `Hi! I'm interested in the following corporate package:

📦 Package: ${pkg.title}
📍 Destination: ${pkg.destination}
👥 Employees: ${pkg.employees}
💰 Price: ${pkg.price}

📋 Details:
${pkg.details}

Please get in touch!`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <>
      <SEO
        title="Corporate Team Building & Events in Jordan | Keepers Experience"
        description="Strengthen your team with outdoor adventures in Jordan. Team building, corporate events, and custom experiences for companies of all sizes."
        image="https://keepersexperience.com/full_logo.png"
        url="https://keepersexperience.com/corporate"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Keepers Experience - Corporate Programs",
          "description": "Corporate team building and outdoor adventures in Jordan",
          "url": "https://keepersexperience.com/corporate",
          "serviceType": "Corporate Team Building",
          "areaServed": "Jordan"
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div
          className={styles.heroBg}
          style={{ backgroundImage: corp.heroImage ? `url('${corp.heroImage}')` : undefined }}
        />
        <div className={`${styles.heroInner} ${heroVisible ? styles.visible : ''}`} ref={heroRef}>
          <button className={styles.backBtn} onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <span className={styles.heroLabel}>Corporate Programs</span>
          <h1 className={styles.heroTitle}>{corp.headline}</h1>
          <p className={styles.heroTagline}>{corp.tagline}</p>
          <p className={styles.heroDesc}>{corp.description}</p>
          <button className={styles.ctaBtn} onClick={() => setFormOpen(true)}>
            Customize Your Own Trip
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section className={styles.servicesSection} ref={servicesRef}>
        <div className={`${styles.sectionInner} ${servicesVisible ? styles.visible : ''}`}>
          <span className={styles.sectionLabel}>What We Offer</span>
          <h2 className={styles.sectionTitle}>Our Programs</h2>
          <div className={styles.servicesGrid}>
            {corp.services.map((s, i) => (
              <div
                key={s.id}
                className={styles.serviceCard}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <span className={styles.serviceNum}>{String(i + 1).padStart(2, '0')}</span>
                <h3 className={styles.serviceTitle}>{s.title}</h3>
                <p className={styles.serviceBody}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PACKAGES ─── */}
      {corp.packages && corp.packages.length > 0 && (
        <section className={styles.packagesSection} ref={packagesRef}>
          <div className={`${styles.sectionInner} ${packagesVisible ? styles.visible : ''}`}>
            <span className={styles.sectionLabel}>Ready-Made</span>
            <h2 className={styles.sectionTitle}>Our Packages</h2>
            <div className={styles.packagesGrid}>
              {corp.packages.map((pkg, i) => (
                <div
                  key={pkg.id}
                  className={styles.packageCard}
                  style={{ animationDelay: `${i * 0.1}s`, cursor: 'pointer' }}
                  onClick={() => navigate(`/corporate/packages/${pkg.slug}`)}
                >
                  <div className={styles.pkgImgWrap}>
                    {pkg.image ? (
                      <img src={pkg.image} alt={pkg.title} className={styles.pkgImg} loading="lazy" />
                    ) : (
                      <div className={styles.pkgImgPlaceholder} />
                    )}
                    <div className={styles.pkgImgOverlay} />
                    <span className={styles.pkgCorpBadge}>Corporate</span>
                    <span className={styles.pkgPriceBadge}>{pkg.price}</span>
                  </div>
                  <div className={styles.pkgBody}>
                    <h3 className={styles.packageTitle}>{pkg.title}</h3>
                    <div className={styles.packageMeta}>
                      <span className={styles.packageMetaItem}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {pkg.destination}
                      </span>
                      <span className={styles.packageMetaItem}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                        {pkg.employees} employees
                      </span>
                    </div>
                    <p className={styles.packageDetails}>{pkg.details}</p>
                    <button
                      className={styles.packageWhatsApp}
                      onClick={(e) => { e.stopPropagation(); bookPackageWhatsApp(pkg); }}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Book via WhatsApp
                    </button>
                  </div>
                </div>
              ))}


            </div>
          </div>
        </section>
      )}

      {/* ─── CLIENTS ─── */}
      {corp.clients && corp.clients.length > 0 && (
        <section className={styles.clientsSection} ref={clientsRef}>
          <div className={`${styles.sectionInner} ${clientsVisible ? styles.visible : ''}`}>
            <span className={styles.sectionLabel}>Trusted By</span>
            <h2 className={styles.sectionTitle}>Our Clients</h2>
            <div className={`${styles.clientsGrid} ${corp.clients.length >= 5 ? styles.clientsCarousel : ''}`}>
              {corp.clients.length > 20 ? (
                <>
                  {/* First half - left to right */}
                  {corp.clients.slice(0, Math.ceil(corp.clients.length / 2)).map((client, i) => (
                    <div
                      key={client.id}
                      className={`${styles.clientLogoWrap} ${styles.carouselLeft}`}
                      style={{ animationDelay: `${i * 0.08}s` }}
                    >
                      <img
                        src={client.logo}
                        alt={client.name}
                        title={client.name}
                        className={styles.clientLogo}
                      />
                    </div>
                  ))}
                  {/* Second half - right to left */}
                  {corp.clients.slice(Math.ceil(corp.clients.length / 2)).map((client, i) => (
                    <div
                      key={client.id}
                      className={`${styles.clientLogoWrap} ${styles.carouselRight}`}
                      style={{ animationDelay: `${i * 0.08}s` }}
                    >
                      <img
                        src={client.logo}
                        alt={client.name}
                        title={client.name}
                        className={styles.clientLogo}
                      />
                    </div>
                  ))}
                </>
              ) : (
                corp.clients.map((client, i) => (
                  <div
                    key={client.id}
                    className={`${styles.clientLogoWrap} ${corp.clients.length >= 5 ? styles.carouselLeft : ''}`}
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <img
                      src={client.logo}
                      alt={client.name}
                      title={client.name}
                      className={styles.clientLogo}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─── FORM MODAL ─── */}
      {formOpen && <TripForm onClose={() => setFormOpen(false)} />}
      <Footer />
    </div>
    </>
  );
}
